import React, { useEffect, useId, useMemo, useRef } from 'react';
import { LinkTrackable } from 'components/Trackable/LinkTrackable';
import { ImageAspectRatio } from 'types/Common';
import { StripeMixedLoadingUI } from 'components/Stripes/StripeMixed/StripeMixedLoadingUI';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { LinkShowAll } from 'components/LinkShowAll/LinkShowAll';
import { buildCollectionPageUrl, slugify, collectionSlug, stripOAuthCallbackParams } from 'utils/fnUrl';
import icons from 'style';
import { useScreenState } from 'hooks/useScreenState/useScreenState';
import { ArrowIcon } from 'components/Lane/Lane.css';
import { Lane } from 'components/Lane/Lane';
import { StripeCardVariantProvider } from 'components/Cards/Card/StripeCardVariantContext';
import { CardVariant } from 'components/Cards/Card/cardVariants';
import { CoverAsset } from 'types/Asset';
import { isMenuTabItem, isPageItem } from 'utils/fnEditorial';
import translate from 'utils/fnTranslate';
import { getCollectionNavigationPath } from 'utils/fnCollections';
import { getExpandablePagePath } from 'utils/fnPages';
import { useCollectionLocationParams } from 'hooks/useCollectionLocationParams';
import { EditorialItem } from 'types/Config';
import { useNavigation } from 'hooks/useNavigation';
import { StripePresenterSelector } from 'components/PresenterSelector/StripePresenterSelector';
import { getLocalStorage } from 'utils/fnStorage';
import { CollectionRoute } from 'types/RouteTypes';
import { useApp } from 'providers/useApp/AppContext';
import { useNavigationData } from 'providers/useNavigation/NavigationContext';
import { LiveIndicator, ShowAllButton, SliderTitle, SliderTitleText, SliderWrapper, TilesContainer } from './Slider.css';

const MIN_CARDS_FOR_SHOW_ALL = 20;

type SliderProps = {
    title: string;
    collectionId?: string | null;
    expandPageId?: string;
    expandable?: boolean;
    miniEpg?: boolean;
    editorialAll?: EditorialItem;
    assets: CoverAsset[];
    isLoading?: boolean;
    stripePosition: number;
    screenName: string;
    disableUiStoreState?: boolean;
    initScrollTo?: number;
    resetScroll?: boolean;
    module?: any;
    variant?: string;
    cardVariant?: CardVariant | null;
    hideTitle?: boolean;
    aspectRatio?: string;
    hasMoreAssets?: boolean;
    totalCount?: number;
    moduleType?: string;
    menuTabId?: string;
    hideChannelLogo?: boolean;
    showEpisodeDescription?: boolean;
    hideGenre?: boolean;
    onCardClick?: (event: React.MouseEvent<any>, data: any) => void;
    onVisibleIndexesChanged?: (first: number, last: number) => void;
    scrollPercentage?: (percentage) => void;
    onScrollRightEdgeReached?: () => void;
    onScrollLeftEdgeReached?: () => void;
    onScrollStopped?: (scrollPosition) => void;
};

export const Slider: React.FC<SliderProps> = ({
    title,
    collectionId,
    expandPageId,
    screenName,
    assets,
    editorialAll,
    expandable,
    stripePosition = 0,
    disableUiStoreState = false,
    initScrollTo,
    resetScroll,
    isLoading,
    moduleType,
    onCardClick,
    onVisibleIndexesChanged = () => {},
    onScrollStopped = () => {},
    scrollPercentage = () => {},
    onScrollLeftEdgeReached = () => {},
    onScrollRightEdgeReached = () => {},
    variant,
    cardVariant,
    hideTitle = false,
    aspectRatio,
    menuTabId,
    hideChannelLogo,
    showEpisodeDescription,
    hideGenre,
    hasMoreAssets = false,
    totalCount,
}) => {
    const titleId = useId();
    const { storeState, obtainState } = useScreenState();
    const storeKey = `slider_${stripePosition}${title}`;
    const storedData = !disableUiStoreState ? obtainState(storeKey) : null;
    const hintContainerRef = useRef<HTMLDivElement>(null);
    const isMobile = getLocalStorage('isMobile');
    const { config: appConfig } = useConfig();
    const locationParamsPath = useCollectionLocationParams();
    const { location } = useNavigation();
    const { appLanguage } = useApp();
    const { emptyStates } = useScreenState();
    const isExpandablePage = expandPageId !== null && expandPageId !== undefined && expandPageId !== '';
    const cardCount = totalCount ?? assets?.length ?? 0;
    const hasEnoughCards = cardCount >= MIN_CARDS_FOR_SHOW_ALL || hasMoreAssets;
    const showCollection = (expandable || isExpandablePage) && hasEnoughCards;
    const { getNavigationItems, navigationDataLoaded } = useNavigationData();
    const navigationItems = navigationDataLoaded ? getNavigationItems() : [];
    const isAbsolutePath = expandable;
    const isRecordings = moduleType === 'TV_RECORDINGS';
    const isNowAndNext = moduleType === 'TV_NOW_AND_NEXT';
    const isVodPurchases = moduleType === 'VOD_PURCHASES';
    const isLive = moduleType === 'TV_NOW_NEXT';
    const isContinueWatching = moduleType === 'CONTINUE_WATCHING';
    const isWatchlist = moduleType === 'WATCHLIST';
    const isCardChannel = moduleType === 'TV_CHANNEL';

    let showMorePath;

    const titleSlug = title ? slugify(title) : undefined;

    if (showCollection) {
        if (menuTabId) {
            showMorePath = `/${menuTabId.toLowerCase()}`;
        } else if (isRecordings) {
            showMorePath = CollectionRoute[appLanguage]?.RECORDING;
        } else if (isVodPurchases) {
            showMorePath = CollectionRoute[appLanguage]?.VOD_PURCHASES;
        } else if (isNowAndNext || isLive) {
            showMorePath = CollectionRoute[appLanguage]?.NOW;
        } else if (isContinueWatching) {
            showMorePath = CollectionRoute[appLanguage]?.CONTINUE_WATCHING;
        } else if (isWatchlist) {
            showMorePath =
                aspectRatio === '2:3' ? CollectionRoute[appLanguage]?.WATCHLIST_MOVIES : CollectionRoute[appLanguage]?.WATCHLIST_SERIES;
        } else if (isExpandablePage) {
            showMorePath = getExpandablePagePath(expandable, expandPageId, titleSlug, navigationItems);
        } else if (!isAbsolutePath || collectionId) {
            showMorePath = getCollectionNavigationPath(expandable, appConfig, collectionId, editorialAll, collectionSlug(title));
        }
    }

    const hasTitle = !hideTitle && title != null && title.length > 0;

    if (!isPageItem(editorialAll) && !isMenuTabItem(editorialAll) && showMorePath && !isRecordings) {
        showMorePath = buildCollectionPageUrl(locationParamsPath, showMorePath);

        const urlQueryParams = stripOAuthCallbackParams(location.search).toString();

        if (urlQueryParams) {
            showMorePath += `?${urlQueryParams}`;
        }
    }

    const navigateToValue = useMemo(() => (initScrollTo ? { number: initScrollTo, smooth: false } : null), [initScrollTo]);

    useEffect(() => {
        if (resetScroll) {
            storeState(storeKey, 0);
        }
    }, [resetScroll]);

    if (resetScroll || isLoading) {
        return <StripeMixedLoadingUI aspectRatio={aspectRatio} moduleType={moduleType} cardVariant={cardVariant} withTitle={hasTitle} />;
    }

    const cardPresenters = StripePresenterSelector.create()
        .setPosition(stripePosition)
        .setTitle(translate(title))
        .setScreenName(translate(screenName))
        .setCardClickListener(onCardClick)
        .setAssets(assets ?? [])
        .setVariant(variant)
        .setAspectRatio(aspectRatio as ImageAspectRatio)
        .setHideChannelLogo(hideChannelLogo)
        .setShowEpisodeDescription(showEpisodeDescription)
        .setHideGenre(hideGenre)
        .get();

    return !(assets?.length > 0)
        ? null
        : cardPresenters?.length > 0 && (
              <SliderWrapper ref={hintContainerRef} isMobile={isMobile}>
                  {hideTitle ? null : showCollection && showMorePath ? (
                      <SliderTitle>
                          <LinkTrackable
                              to={showMorePath}
                              state={{ collectionTitle: translate(title), collectionAspectRatio: aspectRatio }}
                              useAbsolutePath={isAbsolutePath}
                              onClick={() => {
                                  emptyStates(showMorePath);
                              }}
                              data-event-name={'click'}
                              data-track-id={'stripe_view_all'}
                              data-page-name={'Home'}
                              data-module-title={title}
                              visibleFocus="underline"
                          >
                              {isLive && <LiveIndicator />}
                              <SliderTitleText id={titleId} data-test-id={`slider-movieresults-${title}`}>
                                  {translate(title)}
                              </SliderTitleText>
                          </LinkTrackable>
                          <ShowAllButton>
                              <LinkShowAll />
                          </ShowAllButton>
                      </SliderTitle>
                  ) : (
                      <SliderTitle>
                          {isLive && <LiveIndicator />}
                          {title && (
                              <SliderTitleText id={titleId} data-test-id={`slider-movieresults-${title}`}>
                                  {translate(title)}
                              </SliderTitleText>
                          )}
                      </SliderTitle>
                  )}
                  <TilesContainer>
                      <StripeCardVariantProvider variant={cardVariant}>
                          <Lane
                              arrowLeft={<ArrowIcon data-test-id={'tv-detailscreen-castleftarrow'} svg={icons.leftArrowIcon} />}
                              arrowRight={<ArrowIcon data-test-id={'tv-detailscreen-castrightarrow'} svg={icons.rightArrowIcon} />}
                              scrollLeft={resetScroll ? 0 : !disableUiStoreState ? (storedData?.value ?? 0) : -1}
                              isMobile={isMobile}
                              isCardChannel={isCardChannel}
                              navigateTo={navigateToValue}
                              useSnap={cardVariant != null}
                              scrollable={true}
                              asList
                              listLabelledBy={!hideTitle && title ? titleId : undefined}
                              onScrollStopped={(scroll) => {
                                  if (!disableUiStoreState) {
                                      storeState(storeKey, scroll);
                                  }
                                  onScrollStopped(scroll);
                              }}
                              onScrollLeftEdgeReached={onScrollLeftEdgeReached}
                              onScrollRightEdgeReached={onScrollRightEdgeReached}
                              scrolledPercentage={scrollPercentage}
                              onVisibleIndexesChanged={onVisibleIndexesChanged}
                          >
                              {cardPresenters}
                          </Lane>
                      </StripeCardVariantProvider>
                  </TilesContainer>
              </SliderWrapper>
          );
};
